/**
 * Header Search Functionality with Predictive Search
 * Handles the search icon click, input focus, close functionality, and autocomplete
 */
class HeaderSearch {
  constructor() {
    this.searchIcon = document.querySelector('.header-search__icon');
    this.searchContainer = document.querySelector('.header-search__container');
    this.searchInput = document.querySelector('.header-search__input');
    this.closeButton = document.querySelector('.header-search__close');
    
    this.isActive = false;
    this.searchTerm = '';
    this.cachedResults = {};
    this.abortController = new AbortController();
    
    this.init();
  }

  init() {
    if (!this.searchIcon || !this.searchContainer || !this.searchInput) {
      console.warn('Header search elements not found');
      return;
    }

    // Hide loading state by default
    this.hideLoadingState();
    
    this.bindEvents();
  }

  bindEvents() {
    // Search icon click
    this.searchIcon.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleSearch();
    });

    // Close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeSearch();
      });
    }

    // Input focus
    this.searchInput.addEventListener('focus', () => {
      this.openSearch();
    });

    // Input change for predictive search
    this.searchInput.addEventListener('input', (e) => {
      this.onInputChange(e);
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      // Check if we're on mobile (full-screen overlay)
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // On mobile, only close if clicking on the background overlay
        if (e.target === this.searchContainer) {
          this.closeSearch();
        }
      } else {
        // On desktop, close if clicking outside the container
        if (!this.searchContainer.contains(e.target) && 
            !this.searchIcon.contains(e.target)) {
          this.closeSearch();
        }
      }
    });

    // Handle "Search for" button clicks
    this.searchContainer.addEventListener('click', (e) => {
      if (e.target.closest('.predictive-search__item--search-for')) {
        e.preventDefault();
        const searchTerm = this.searchInput.value.trim();
        if (searchTerm) {
          const searchUrl = `/search?q=${encodeURIComponent(searchTerm)}`;
          window.location.href = searchUrl;
        }
      }
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isActive) {
        this.closeSearch();
      }
    });

    // Enter key to submit search
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.submitSearch();
      }
    });
  }

  onInputChange(event) {
    const newSearchTerm = event.target.value.trim();
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Update search term
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.hidePredictiveResults();
      this.hideLoadingState();
      return;
    }

    // Debounce the search
    this.searchTimeout = setTimeout(() => {
      this.getPredictiveSearchResults(this.searchTerm);
    }, 300);
  }

  getPredictiveSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    
    // Check cache first
    if (this.cachedResults[queryKey]) {
      this.renderPredictiveResults(this.cachedResults[queryKey]);
      return;
    }

    // Show loading state
    this.showLoadingState();

    // Abort previous request
    this.abortController.abort();
    this.abortController = new AbortController();

    // Fetch from Shopify predictive search API
    fetch(`${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML || '';
        
        // Cache the results
        this.cachedResults[queryKey] = resultsMarkup;
        this.hideLoadingState();
        this.renderPredictiveResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        console.warn('Predictive search error:', error);
        this.hideLoadingState();
        this.hidePredictiveResults();
      });
  }

  showLoadingState() {
    const loadingState = this.searchContainer.querySelector('[data-predictive-search-loading]');
    if (loadingState) {
      loadingState.style.display = 'block';
    }
  }

  hideLoadingState() {
    const loadingState = this.searchContainer.querySelector('[data-predictive-search-loading]');
    if (loadingState) {
      loadingState.style.display = 'none';
    }
  }

  renderPredictiveResults(resultsMarkup) {
    if (!resultsMarkup) {
      this.hidePredictiveResults();
      return;
    }

    // Create or update predictive results container
    let predictiveContainer = this.searchContainer.querySelector('.header-search__predictive-results');
    if (!predictiveContainer) {
      predictiveContainer = document.createElement('div');
      predictiveContainer.className = 'header-search__predictive-results';
      this.searchContainer.appendChild(predictiveContainer);
    }

    // Extract just the results content, not the entire form
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = resultsMarkup;
    
    // Find the results wrapper
    const resultsWrapper = tempDiv.querySelector('#predictive-search-results-groups-wrapper');
    if (resultsWrapper) {
      predictiveContainer.innerHTML = resultsWrapper.innerHTML;
    } else {
      predictiveContainer.innerHTML = resultsMarkup;
    }

    predictiveContainer.style.display = 'block';
    predictiveContainer.classList.add('is-visible');
  }

  hidePredictiveResults() {
    const predictiveContainer = this.searchContainer.querySelector('.header-search__predictive-results');
    if (predictiveContainer) {
      predictiveContainer.style.display = 'none';
      predictiveContainer.classList.remove('is-visible');
    }
  }

  toggleSearch() {
    if (this.isActive) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    this.isActive = true;
    this.searchContainer.classList.add('is-active');
    
    // Add body class to prevent scrolling on mobile
    document.body.classList.add('search-active');
    
    // On mobile, prevent body scroll
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    }
    
    // Focus the input with a slight delay to ensure the container is visible
    setTimeout(() => {
      this.searchInput.focus();
    }, 100);
  }

  closeSearch() {
    this.isActive = false;
    this.searchContainer.classList.remove('is-active');
    this.searchInput.blur();
    this.searchInput.value = '';
    this.hidePredictiveResults();
    this.hideLoadingState();
    
    // Remove body class and restore scroll
    document.body.classList.remove('search-active');
    document.body.style.overflow = '';
  }

  submitSearch() {
    const query = this.searchInput.value.trim();
    if (query) {
      // Redirect to search results page
      const searchUrl = `/search?q=${encodeURIComponent(query)}`;
      window.location.href = searchUrl;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HeaderSearch();
});
